import { Feature, Map } from "ol";
import { Point } from "ol/geom";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import type TileLayer from "ol/layer/Tile";
import { toLonLat } from "ol/proj";
import type { TileWMS } from "ol/source";
import { documentsApiSlice } from "../../../../../api/rtk/base/slices/documentsApiSlice";
import { tempDocumentsApiSlice } from "../../../../../api/rtk/tempDocuments/slices/tempDocumentsApiSlice";
import { getItemWebStorage } from "../../../../../features/webStorageSaver";
import { showToastMessage } from "../../../../../stateManagement/reducers";
import { store } from "../../../../../stateManagement/store";
import { cursorStyleFunctions } from "../../helpers";
import { getFeatureURLParamsObject } from "../getObjectsOnMapPoint/getObjectsOnMapPoint";
import { trafficPlanPositionMarkerStyle } from "../../styles/mapStyles";

const TRAFFIC_PLAN_EXTENSION = ".trafficplan";

function normalizeExtension(extension: unknown) {
    if (typeof extension !== "string") return "";
    const normalizedExtension = extension.toLowerCase();
    return normalizedExtension.startsWith(".") ? normalizedExtension : `.${normalizedExtension}`;
}

async function getRoadWorkSegmentId(event: any, layer: TileLayer<TileWMS>, map: Map) {
    const source = layer.getSource();
    const params = source?.getParams();
    const view = map.getView();
    const featureInfoUrl = source?.getFeatureInfoUrl(
        event.coordinate,
        view.getResolution()!,
        view.getProjection(),
        getFeatureURLParamsObject({
            VERSION: params?.VERSION,
            LAYERS: params?.LAYERS,
            CQL_FILTER: params?.CQL_FILTER,
            propertyName: "InfoObjectID",
            featureCount: 1,
        }),
    );

    if (!featureInfoUrl) return null;

    const response = await fetch(featureInfoUrl);
    if (!response.ok) throw new Error("Не удалось получить участок ПОДД");

    const data = await response.json();
    const feature = data?.features?.[0];
    return feature?.properties?.InfoObjectID ?? feature?.id?.split(".")?.[1] ?? null;
}

async function getTrafficPlanDocumentId(roadWorkSegmentId: string | number) {
    const request = store.dispatch(documentsApiSlice.endpoints.fetchObjectDocuments.initiate({
        urlType: "objects",
        objectType: "RoadWorkSegments",
        objectId: roadWorkSegmentId,
    }, {
        forceRefetch: true,
    }));

    try {
        const documents = await request.unwrap();
        const trafficPlanDocument = documents?.value?.find(
            (document: any) => normalizeExtension(document?.FileExtension) === TRAFFIC_PLAN_EXTENSION,
        );
        return trafficPlanDocument?.DocumentID ?? null;
    } finally {
        request.unsubscribe();
    }
}

async function createTemporaryTrafficPlanDocument(documentId: string | number) {
    const apiBaseUrl = getItemWebStorage("currentApiUrl");
    const token = store.getState().auth.tokens.app;
    if (!apiBaseUrl || !token) throw new Error("Не удалось определить REST API или токен IndorRoad");

    const request = store.dispatch(tempDocumentsApiSlice.endpoints.createTemporaryIndorRoadDocument.initiate({
        apiBaseUrl,
        documentId,
        token,
    }));

    try {
        return await request.unwrap();
    } finally {
        request.reset();
    }
}

function openTrafficPlan(
    downloadUrl: string,
    latitude: number,
    longitude: number,
    onOpeningChange?: (isOpening: boolean) => void,
) {
    const url = `indortrafficplan://open?url=${downloadUrl}&lat=${latitude}&lon=${longitude}`;
    onOpeningChange?.(true);

    const stopOpening = (showWarning = false) => {
        window.clearTimeout(timeoutId);
        window.removeEventListener("blur", handleWindowBlur);
        onOpeningChange?.(false);

        if (showWarning) {
            store.dispatch(showToastMessage({
                messageText: "Не удалось открыть документ в IndorTrafficPlan. Проверьте, что у вас установлена последняя версия приложения.",
                messageType: "error",
                windowType: "map",
            }));
        }
    };
    const handleWindowBlur = () => stopOpening();
    const timeoutId = window.setTimeout(() => stopOpening(true), 10000);
    window.addEventListener("blur", handleWindowBlur, { once: true });
    window.location.href = url;
}

export function activateTrafficPlanPositionSelection(
    isEnabled: boolean,
    layer?: TileLayer<TileWMS>,
    map?: Map,
    onOpeningChange?: (isOpening: boolean) => void,
) {
    const markerFeature = new Feature();
    markerFeature.setStyle(trafficPlanPositionMarkerStyle);
    let requestIsRunning = false;

    const markerLayer = new VectorLayer({
        zIndex: 6000,
        source: new VectorSource({
            features: [markerFeature],
        }),
    });

    const moveMarker = (event: any) => {
        markerFeature.setGeometry(new Point(event.coordinate));
    };

    const selectPosition = async (event: any) => {
        if (!layer || !map || requestIsRunning) return;
        requestIsRunning = true;
        cursorStyleFunctions.loading();
        onOpeningChange?.(true);

        try {
            const [longitude, latitude] = toLonLat(event.coordinate, map.getView().getProjection());
            const roadWorkSegmentId = await getRoadWorkSegmentId(event, layer, map);
            if (!roadWorkSegmentId) {
                onOpeningChange?.(false);
                store.dispatch(showToastMessage({
                    messageText: "В выбранной точке отсутствует участок ПОДД",
                    messageType: "info",
                    windowType: "map",
                }));
                return;
            }

            const documentId = await getTrafficPlanDocumentId(roadWorkSegmentId);
            if (!documentId) {
                onOpeningChange?.(false);
                store.dispatch(showToastMessage({
                    messageText: "У участка ПОДД отсутствует документ .trafficplan",
                    messageType: "info",
                    windowType: "map",
                }));
                return;
            }

            const temporaryDocument = await createTemporaryTrafficPlanDocument(documentId);
            if (!temporaryDocument.ok || !temporaryDocument.downloadUrl) {
                throw new Error("Temporary document URL was not returned");
            }
            openTrafficPlan(temporaryDocument.downloadUrl, latitude, longitude, onOpeningChange);
        } catch (error) {
            onOpeningChange?.(false);
            console.error("TrafficPlan document preparation error:", error);
            store.dispatch(showToastMessage({
                messageText: "Не удалось подготовить документ ПОДД",
                messageType: "error",
                windowType: "map",
            }));
        } finally {
            cursorStyleFunctions.default();
            requestIsRunning = false;
        }
    };

    if (isEnabled && layer) {
        map?.addLayer(markerLayer);
        map?.on("pointermove", moveMarker);
        map?.on("singleclick", selectPosition);
    }

    return {
        off: () => {
            map?.removeLayer(markerLayer);
            map?.un("pointermove", moveMarker);
            map?.un("singleclick", selectPosition);
        }
    };
}
